import React, { ReactElement, memo } from 'react';
import styled, { CSSObject } from '@emotion/styled';

import Avatar from '../../../atoms/Avatar';
import { Link } from 'react-router-dom';
import { spacing, getBorders, type, getTextColor, getTextType, getBackgroundTheme } from '../../../style';
import { displayUserLocation, tsSince, renderAvatar } from '../../../common';
import { vysionTrack } from '../../../utils/vysion/vysionService';
import { isDarkMode, lazilyEvaluateVal } from '../../../utils/helpers';
import {
  HeartNotificationIcon,
  PrivatePhotoIcon,
  PublicPhotoIcon,
  StarIcon,
  OfferNew,
} from '../../../atoms/icons/favoriteIcons';
import FavoriteActionButton from './FavoriteActionButton';
import { auth10tixVerification } from '../../../config/Master';
import useMixPanel from '../../../hooks/useMixPanel';
import useCompleteProfileModal from '../../../hooks/useCompleteProfileModal';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../constants/mixpanel';
import { favToggle, storeFavChange } from '../../../actions/favActions';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { mixpanelTrackEvent } from '../../../utils/mixpanel/common';
import { CustomStyledProps } from 'style/style';
import VerifiedBadgeTooltip from '../../common/VerifiedBadgeTooltip';
import breakpoints from 'theme/material/breakpoints';
import { FONT_FAMILIES } from 'theme/material/constants';

const ResultItem = styled('div')<{ backgroundColor: any }>(
  () => ({
    ...getTextColor().DARKER,
    display: 'inline-flex',
    backgroundColor: getBackgroundTheme().block,
    flex: '1 0 100%',
    position: 'relative',
    [breakpoints.down(639.05)]: {
      flex: '1 0 40%',
    },
    paddingBottom: 20,
    paddingTop: 20,
    borderBottom: getBorders().DEFAULT,
  }),
  ({ backgroundColor }) => ({
    backgroundColor: `${backgroundColor}`,
  })
);

const TitleContainer = styled('div')(() => ({
  width: '100%',
  color: isDarkMode() ? '#FFFFFF' : '#212121',
  display: 'flex',
  alignItems: 'center',
}));

const Title = styled('div')(() => ({
  ...type.title.XS,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontFamily: FONT_FAMILIES.secondary,
  letterSpacing: '-0.5px',
  [breakpoints.down(639.05)]: {
    fontSize: 16,
  },
}));

const Meta = styled('div')(() => ({
  ...getTextType().caption,
  letterSpacing: '-0.3px',
  ...spacing.MARGIN_BOTTOM_SM,
  ...getTextColor().LIGHT_GRAY,
  [breakpoints.down(639.05)]: {
    fontSize: 10,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    letterSpacing: 'none',
    ...spacing.MARGIN_BOTTOM_XS,
  },
}));

const Details = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexGrow: 1,
  marginRight: 12,
  ...getTextType().caption,
  height: 100,
  [breakpoints.down(768.05)]: {
    maxWidth: 160,
  },
  [breakpoints.down(639.05)]: {
    height: 64,
    maxWidth: 140,
    marginRight: 6,
  },
  [breakpoints.down(374.05)]: {
    maxWidth: 80,
  },
}));

const Wrapper = styled(Link)({
  flexGrow: 1,
  display: 'flex',
  marginLeft: 20,
  [breakpoints.down(639.05)]: {
    marginLeft: 10,
  },
});

const Timestamp = styled('div')(() => ({
  fontSize: 13,
  ...getTextColor().LIGHT_GRAY,
  letterSpacing: '-0.3px',
  [breakpoints.down(639.05)]: {
    fontSize: 10,
    marginTop: 0,
  },
}));

const PhotoCountDetails = styled('div')({});

const PhotoCount = styled('span')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  [breakpoints.down(639.05)]: {
    gap: 3,
  },
});

const StyledPhotoCount = styled('span')({
  ...getTextColor().LIGHT_GRAY,
  fontSize: 12,
  lineHeight: '15px',
  letterSpacing: -0.3,
  [breakpoints.down(639.05)]: {
    fontSize: 11,
  },
});

const StyledPublicPhotoCount = styled('span')({
  borderRight: '1px solid #E21D4A',
  paddingRight: 5,
  ...getTextColor().LIGHT_GRAY,
  fontSize: 12,
  lineHeight: '15px',
  letterSpacing: -0.3,
  [breakpoints.down(639.05)]: {
    fontSize: 11,
  },
});

const Icon = styled('span')({
  height: 11,
  width: 12,
  fontSize: 0,
  '& svg': {
    height: 11,
    width: 12,
  },
  [breakpoints.down(639.05)]: {
    height: 10,
    width: 11,
    '& svg': {
      height: 10,
      width: 11,
    },
  },
});

const OnlineDot = styled('div')({
  height: '8px',
  width: '8px',
  backgroundColor: 'lightgreen',
  borderRadius: '50%',
  display: 'inline-block',
});

const StyledStatusBannerText = styled('span')<{ customColor: string }>(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    fontWeight: 600,
    fontStyle: 'italic',
    lineHeight: '19px',
    letterSpacing: '-0.3px',
    display: 'inline-block',
    position: 'relative',
    '::after': {
      content: '"•"',
      display: 'inline-block',
      ...getTextColor().DARKER,
      margin: '0 4px',
      float: 'right',
    },
    svg: {
      height: 12,
    },
    [breakpoints.down(639.05)]: {
      fontSize: 10,
      lineHeight: '15px',
      svg: {
        display: 'none',
      },
    },
  }),
  ({ customColor }: CustomStyledProps) => ({
    color: `${customColor}`,
  })
);

const VerifiedBadge = styled('span')(
  (): CSSObject => ({
    marginLeft: 3,
    zIndex: 2,
    fontSize: 0,
    height: 14,
    width: 14,
  })
);

interface FavoritesProfileListViewProps {
  favKey: string;
  profile: any;
  title: string;
  hashId: string;
  isFavorite: boolean;
  isFavoritedMe: boolean;
  isMatched: boolean;
  page: string;
  timestamp: string;
  offer: any;
  pageTitle?: string;
}

const FavoritesProfileListView = ({
  favKey,
  profile,
  title,
  hashId,
  isFavorite,
  isFavoritedMe,
  isMatched,
  page,
  timestamp,
  offer,
  pageTitle,
}: FavoritesProfileListViewProps): ReactElement => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.profile);

  const { showMandatoryCompleteProfile } = useCompleteProfileModal();
  const { trackProfileView, setMixpanelDataByKeyHandler } = useMixPanel();

  const getInfoText = (profile, otherProfile) => {
    if (!profile || !otherProfile) return '';

    const userLoc = {
      country: profile.country,
      region: profile.region,
      city: profile.city,
    };

    const otherUserLoc = {
      country: otherProfile.country,
      region: otherProfile.region,
      city: otherProfile.city,
    };

    return `${otherProfile.age} • ${displayUserLocation(userLoc, otherUserLoc)}`;
  };

  const getStatusBanner = (isCustomBackground = false) => {
    const darkMode = isDarkMode();

    if (offer !== undefined && offer.data !== undefined) {
      if (offer.data.status === 'accepted') {
        if (isCustomBackground) {
          return darkMode ? 'rgba(19,131,210,0.4)' : '#F3FCFC';
        }

        return (
          <StyledStatusBannerText
            data-test-id="fav-banner-offer-accepted"
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            customColor="rgba(19, 131, 210, 1)"
          >
            <HeartNotificationIcon fill="rgba(19, 131, 210, 1)" /> Offer Accepted!
          </StyledStatusBannerText>
        );
      }

      if (offer.data.status === 'pending') {
        if (isCustomBackground) {
          return darkMode ? 'rgba(191,146,0,0.4)' : 'rgba(255, 175, 0, 0.08)';
        }

        if (offer.data.to_account_hash_id === user.hash_id) {
          return (
            <StyledStatusBannerText
              data-test-id={offer.data.suggest ? 'fav-banner-suggest-request' : 'fav-banner-offer-request'}
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              customColor="rgba(191, 146, 0, 1)"
            >
              {offer.data.suggest ? 'Asked you to Offer!' : 'Sent You an Offer!'}
            </StyledStatusBannerText>
          );
        }

        return (
          <StyledStatusBannerText
            data-test-id="fav-banner-pending-offer"
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            customColor="rgba(191, 146, 0, 1)"
          >
            <OfferNew fill="rgba(191, 146, 0, 1)" />
            Pending Offer
          </StyledStatusBannerText>
        );
      }
    }

    if (isMatched) {
      if (isCustomBackground) {
        return darkMode ? 'rgba(208, 99, 105, 0.4)' : '#FEF4F6';
      }

      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <StyledStatusBannerText data-test-id="fav-banner-mutual-favorite" customColor="#E21D4A">
          <StarIcon isActive={false} fill="#E21D4A" /> Mutual Favorite!
        </StyledStatusBannerText>
      );
    }

    if (isFavoritedMe && (page === 'from' || page === 'viewed_me')) {
      if (isCustomBackground) {
        return darkMode ? 'rgba(208, 99, 105, 0.4)' : '#FEF4F6';
      }

      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <StyledStatusBannerText data-test-id="fav-banner-favorited-you" customColor="#E21D4A">
          <StarIcon isActive={false} fill="#E21D4A" /> Favorited You!
        </StyledStatusBannerText>
      );
    }

    if (isCustomBackground) {
      return darkMode ? 'transparent' : 'rgba(255, 255, 255, 1)';
    }

    return '';
  };

  const profileData = profile.profile.data;
  const extraInfo = page === 'to' ? 'favoritedYou' : 'yourFavorites';
  const avatar =
    lazilyEvaluateVal(profileData, 3, false, 'avatar', 'data', 'urls') && renderAvatar(profileData.avatar.data.urls);

  const photoCount = parseInt(profileData.approved_public_photos_count);
  const privatePhotoCount = profileData.private_photos_count ? parseInt(profileData.private_photos_count) : 0;
  const userVerified = profileData.id_verified;
  const isSelfieVerified = profileData.selfie_verified;

  const trackCardClickEvent = () => {
    vysionTrack({
      action: 'click',
      component: 'Profile Card',
      extraInfo,
      page: 'Favorites',
    });
    trackProfileView({ Source: pageTitle, User: hashId });

    let mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewDirect;

    if (pageTitle === 'Viewed Me') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewViewedMe;
    } else if (pageTitle === 'Favorited Me') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewFavoritedMe;
    } else if (pageTitle === 'My Favorites') {
      mxOfferMadeSourceValue = TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewMyFavorites;
    }

    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.OFFER_MADE,
      payload: { source: mxOfferMadeSourceValue },
    });
  };

  const handleMixpanelProfileFavorited = () => {
    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PROFILE_FAVORITED,
      payload: {
        eventProperties: {
          Source: pageTitle,
          User: hashId,
        },
        incrementalProperties: {
          '# favorites': 1,
        },
      },
    });
  };

  const toggleTracking = () => {
    if (isFavorite) {
      dispatch(storeFavChange(favKey, page));
      dispatch(favToggle(hashId));
      return;
    }

    vysionTrack({
      action: 'click',
      component: isFavorite ? 'unfavorite' : 'favorite',
      extraInfo,
      page: 'Favorites',
    });

    // when user favorites
    // check if capable
    showMandatoryCompleteProfile(
      () => {
        dispatch(storeFavChange(favKey, page));
        dispatch(favToggle(hashId));
        handleMixpanelProfileFavorited();
      },
      () => undefined,
      'Add to Favorites'
    );

    if (!isFavorite) {
      handleMixpanelProfileFavorited();
    }
  };

  return (
    <ResultItem className="css-favorite-list-item" backgroundColor={getStatusBanner(true)}>
      <Wrapper to={`/profile/${hashId}`} onClick={trackCardClickEvent}>
        <Avatar
          src={avatar}
          width="100px"
          alt="Avatar photo"
          customStyle={{
            ...spacing.MARGIN_RIGHT_MD,
            flex: '0 0 100px',
            height: 100,
            [breakpoints.down(639.05)]: {
              flex: '0 0 65px',
              width: 65,
              height: 65,
              marginRight: 8,
            },
            border: isMatched ? '2px solid #E21D4A' : '1px solid rgba(218, 225, 233, 1)',
          }}
        />
        <Details>
          <div>
            <TitleContainer>
              <Title>{title}</Title>
              {auth10tixVerification && userVerified && (
                <VerifiedBadge data-test-id="data-verified-badge">
                  <VerifiedBadgeTooltip
                    showToolTip={true}
                    isIdVerified={userVerified}
                    isSelfieVerified={isSelfieVerified}
                    customVerifiedBadgeStyle={{ top: 0, [breakpoints.down(639.05)]: { top: 0 } }}
                  />
                </VerifiedBadge>
              )}
            </TitleContainer>
            <Meta>
              {profileData.online ? <OnlineDot /> : ''} {getInfoText(user, profileData)}
            </Meta>
            <PhotoCountDetails data-test-id="data-member-photo-count">
              <PhotoCount>
                <Icon data-test-id="data-camera-icon">
                  <PublicPhotoIcon />
                </Icon>
                <StyledPublicPhotoCount>{photoCount}</StyledPublicPhotoCount>
                <Icon data-test-id="data-eye-hidden-icon">
                  <PrivatePhotoIcon />
                </Icon>
                <StyledPhotoCount>{privatePhotoCount}</StyledPhotoCount>
              </PhotoCount>
            </PhotoCountDetails>
          </div>
          <Timestamp>
            {getStatusBanner()}
            {tsSince(timestamp)}
          </Timestamp>
        </Details>
      </Wrapper>
      <FavoriteActionButton
        favorited={isFavorite}
        toggleTracking={toggleTracking}
        page={page}
        offer={offer}
        otherAccount={profile}
        isMatched
        favKey={favKey}
        viewType="list"
        isSuggestRequest={offer && offer.data && offer.data.suggest}
        pageTitle={pageTitle}
      />
    </ResultItem>
  );
};

export default memo(FavoritesProfileListView);
